import React, { useCallback, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { connect, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  billingSummaryRequest,
  billingSubscriptionsRequest,
  billingPaymentsRequest,
} from "./redux/billingDispatcher";
import { cur } from "../../constant/Constants";
import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, postData } from "../../services/api";
import DealerBillingPanel from "./DealerBillingPanel";
import BillingSettings from "./BillingSettings";

const money = (v) =>
  `${cur}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");

/* One headline number with its caption. `alert` turns it red - used for the
   count of sellers sitting on a paid tier with no way to charge them, which is
   the number this whole screen exists to surface. */
const StatCard = ({ label, value, sub, alert }) => (
  <Paper
    sx={{
      p: 2,
      flex: "1 1 180px",
      minWidth: 180,
      borderLeft: `4px solid ${alert ? "#d32f2f" : "#1976d2"}`,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 600, color: alert ? "#d32f2f" : "inherit" }}>
      {value}
    </Typography>
    {sub ? (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    ) : null}
  </Paper>
);

const stateColour = (s) => {
  if (s === "Active") return "success";
  if (s === "Free tier") return "default";
  return "error";
};

const INVOICE_COLOUR = {
  paid: "success", open: "info", overdue: "error",
  void: "default", refunded: "warning", draft: "default",
};

const TAB = { SUBS: 0, PAYMENTS: 1, INVOICES: 2, NOTICES: 3, SETTINGS: 4 };

function BillingOverview({
  summary,
  subscriptions,
  totalSubscriptions,
  payments,
  totalPayments,
  loading,
}) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(TAB.SUBS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [unbilledOnly, setUnbilledOnly] = useState(false);

  /* The drill-down. Held here rather than in the grid so it survives a
     refresh of the list underneath it. */
  const [openDealer, setOpenDealer] = useState(null);

  /* Invoices and notices are fetched straight rather than through redux -
     they are only ever read on this screen. */
  const [invoices, setInvoices] = useState({ rows: [], total: 0, paid: 0, outstanding: 0 });
  const [notices, setNotices] = useState({ rows: [], total: 0 });
  const [localLoading, setLocalLoading] = useState(false);
  const [confirmRun, setConfirmRun] = useState(null);

  const refreshSummary = useCallback(() => dispatch(billingSummaryRequest()), [dispatch]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  const loadInvoices = useCallback(async () => {
    setLocalLoading(true);
    try {
      const res = await fetchData(
        `${ApiURLs.BILLING_INVOICES}?limit=${pageSize}&offset=${page + 1}&search=${encodeURIComponent(search)}`
      );
      if (res.status === 1) {
        setInvoices({
          rows: res.data.result || [],
          total: res.data.total || 0,
          paid: res.data.paid_total || 0,
          outstanding: res.data.outstanding_total || 0,
        });
      }
    } catch (e) { /* handled by the helper */ }
    setLocalLoading(false);
  }, [page, pageSize, search]);

  const loadNotices = useCallback(async () => {
    setLocalLoading(true);
    try {
      const res = await fetchData(
        `${ApiURLs.BILLING_NOTIFICATIONS}?limit=${pageSize}&offset=${page + 1}`
      );
      if (res.status === 1) setNotices({ rows: res.data.result || [], total: res.data.total || 0 });
    } catch (e) { /* handled */ }
    setLocalLoading(false);
  }, [page, pageSize]);

  /* All tabs share one page/pageSize, so switching tab starts at page 1 again
     rather than asking for page 4 of a list that has one page. */
  useEffect(() => {
    const t = setTimeout(() => {
      if (tab === TAB.SUBS) dispatch(billingSubscriptionsRequest(page + 1, pageSize, search, unbilledOnly));
      else if (tab === TAB.PAYMENTS) dispatch(billingPaymentsRequest(page + 1, pageSize, search));
      else if (tab === TAB.INVOICES) loadInvoices();
      else if (tab === TAB.NOTICES) loadNotices();
    }, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [dispatch, tab, page, pageSize, search, unbilledOnly, loadInvoices, loadNotices]);

  const reloadCurrent = () => {
    refreshSummary();
    if (tab === TAB.SUBS) dispatch(billingSubscriptionsRequest(page + 1, pageSize, search, unbilledOnly));
    else if (tab === TAB.PAYMENTS) dispatch(billingPaymentsRequest(page + 1, pageSize, search));
    else if (tab === TAB.INVOICES) loadInvoices();
    else if (tab === TAB.NOTICES) loadNotices();
  };

  const runMonthly = async () => {
    setConfirmRun(null);
    try {
      const res = await postData({}, ApiURLs.BILLING_RUN_MONTHLY);
      if (res.status === 1) { toast.success(res.message); reloadCurrent(); }
    } catch (e) { /* handled */ }
  };

  const runCreditControl = async () => {
    try {
      const res = await postData({}, ApiURLs.BILLING_RUN_CREDIT_CONTROL);
      if (res.status === 1) { toast.success(res.message); reloadCurrent(); }
    } catch (e) { /* handled */ }
  };

  const openRow = (row) => setOpenDealer(row.user_id || row.id);

  const subscriptionColumns = [
    { field: "seller", headerName: "Seller", width: 220 },
    { field: "account_number", headerName: "Account", width: 100, renderCell: (p) => p.value || "-" },
    { field: "email", headerName: "Email", width: 210 },
    { field: "tier_name", headerName: "Tier", width: 120 },
    { field: "sector", headerName: "Sector", width: 100 },
    {
      field: "tier_price",
      headerName: "Per month",
      width: 110,
      renderCell: (p) => (Number(p.value) > 0 ? money(p.value) : "Free"),
    },
    {
      field: "billing_state",
      headerName: "Billing",
      width: 210,
      renderCell: (p) => <Chip size="small" label={p.value} color={stateColour(p.value)} />,
    },
    {
      field: "paid_total",
      headerName: "Paid to date",
      width: 120,
      renderCell: (p) => money(p.value),
    },
    {
      field: "last_paid_at",
      headerName: "Last payment",
      width: 130,
      renderCell: (p) => shortDate(p.value),
    },
    { field: "live_ads", headerName: "Live ads", width: 95 },
    {
      field: "_open",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => openRow(p.row)}>Open</Button>
      ),
    },
  ];

  const paymentColumns = [
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      renderCell: (p) => (p.value ? new Date(p.value).toLocaleString() : "-"),
    },
    { field: "seller", headerName: "Seller", width: 190 },
    { field: "email", headerName: "Email", width: 210 },
    { field: "paid_for", headerName: "Paid for", width: 150 },
    { field: "ad_title", headerName: "Ad", width: 200, renderCell: (p) => p.value || "-" },
    {
      field: "amount",
      headerName: "Amount",
      width: 110,
      renderCell: (p) => money(p.value),
    },
    { field: "gateway", headerName: "Via", width: 105 },
    {
      field: "status",
      headerName: "Status",
      width: 115,
      renderCell: (p) => (
        <Chip size="small" label={p.value} color={/succe/i.test(p.value) ? "success" : "warning"} />
      ),
    },
    { field: "transaction_id", headerName: "Reference", width: 240 },
    {
      field: "_open", headerName: "", width: 100, sortable: false,
      renderCell: (p) => <Button size="small" onClick={() => openRow(p.row)}>Open</Button>,
    },
  ];

  const invoiceColumns = [
    { field: "invoice_number", headerName: "Number", width: 120 },
    { field: "seller", headerName: "Seller", width: 200 },
    { field: "account_number", headerName: "Account", width: 100, renderCell: (p) => p.value || "-" },
    { field: "description", headerName: "For", width: 210 },
    {
      field: "period_start", headerName: "Period", width: 180,
      renderCell: (p) => (p.value ? `${shortDate(p.value)} - ${shortDate(p.row.period_end)}` : "-"),
    },
    { field: "total", headerName: "Total", width: 110, renderCell: (p) => money(p.value) },
    {
      field: "status", headerName: "Status", width: 130,
      renderCell: (p) => (
        <>
          <Chip size="small" label={p.value} color={INVOICE_COLOUR[p.value] || "default"} />
          {p.row.returned_at && (
            <Tooltip title={`Direct Debit returned: ${p.row.returned_reason || "no reason given"}`}>
              <Chip size="small" sx={{ ml: 0.5 }} variant="outlined" color="error" label="returned" />
            </Tooltip>
          )}
        </>
      ),
    },
    { field: "due_date", headerName: "Due", width: 110, renderCell: (p) => shortDate(p.value) },
    { field: "paid_at", headerName: "Paid", width: 110, renderCell: (p) => shortDate(p.value) },
    { field: "gateway", headerName: "Via", width: 100, renderCell: (p) => p.value || "-" },
    {
      field: "_open", headerName: "", width: 100, sortable: false,
      renderCell: (p) => <Button size="small" onClick={() => setOpenDealer(p.row.user_id)}>Open</Button>,
    },
  ];

  const noticeColumns = [
    { field: "created_at", headerName: "Raised", width: 160, renderCell: (p) => (p.value ? new Date(p.value).toLocaleString() : "-") },
    { field: "seller", headerName: "Seller", width: 200 },
    { field: "account_number", headerName: "Account", width: 100, renderCell: (p) => p.value || "-" },
    { field: "invoice_number", headerName: "Invoice", width: 120, renderCell: (p) => p.value || "-" },
    { field: "subject", headerName: "Subject", width: 300 },
    { field: "amount", headerName: "Amount", width: 110, renderCell: (p) => (p.value ? money(p.value) : "-") },
    { field: "deadline_date", headerName: "Deadline", width: 115, renderCell: (p) => shortDate(p.value) },
    {
      field: "status", headerName: "Status", width: 160,
      renderCell: (p) => (
        <>
          <Chip size="small" label={p.value}
            color={p.value === "sent" ? "success" : p.value === "resolved" ? "default"
              : p.value === "failed" ? "error" : "warning"} />
          {p.row.escalated_at && <Chip size="small" sx={{ ml: 0.5 }} variant="outlined" color="error" label="escalated" />}
        </>
      ),
    },
    {
      field: "_open", headerName: "", width: 100, sortable: false,
      renderCell: (p) => <Button size="small" onClick={() => setOpenDealer(p.row.user_id)}>Open</Button>,
    },
  ];

  let rows = [];
  let columns = subscriptionColumns;
  let rowCount = 0;
  if (tab === TAB.SUBS) {
    rows = (subscriptions || []).map((r, i) => ({ id: r.user_id || i, ...r }));
    rowCount = totalSubscriptions || 0;
  } else if (tab === TAB.PAYMENTS) {
    rows = (payments || []).map((r, i) => ({ id: r.id || i, ...r }));
    columns = paymentColumns;
    rowCount = totalPayments || 0;
  } else if (tab === TAB.INVOICES) {
    rows = (invoices.rows || []).map((r, i) => ({ id: r.id || i, ...r }));
    columns = invoiceColumns;
    rowCount = invoices.total;
  } else if (tab === TAB.NOTICES) {
    rows = (notices.rows || []).map((r, i) => ({ id: r.id || i, ...r }));
    columns = noticeColumns;
    rowCount = notices.total;
  }

  return (
    <Box sx={{ height: "95%", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {summary && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <StatCard
            label="On a paid tier"
            value={summary.sellers_on_a_paid_tier}
            sub={`${summary.sellers_on_a_tier} sellers on a tier in total`}
          />
          <StatCard
            label="No payment method"
            value={summary.paid_tier_no_mandate}
            sub="on a paid tier, cannot be charged"
            alert={summary.paid_tier_no_mandate > 0}
          />
          <StatCard
            label="Billed monthly on paper"
            value={money(summary.monthly_value_on_paper)}
            sub="what the tiers add up to"
          />
          <StatCard
            label="Collected, last 30 days"
            value={money(summary.collected_last_30_days)}
            sub={`${summary.payments_last_30_days} payments`}
          />
          <StatCard
            label="Collected, all time"
            value={money(summary.collected_all_time)}
            sub={`${summary.payments_all_time} payments, last on ${shortDate(summary.last_payment_at)}`}
          />
        </Box>
      )}

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2, pt: 1 }}>
          <Tabs
            value={tab}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(e, v) => { setTab(v); setPage(0); }}
          >
            <Tab label="Subscriptions" />
            <Tab label="Payments" />
            <Tab label="Invoices" />
            <Tab label="Notices" />
            <Tab label="Settings & wording" />
          </Tabs>

          {tab !== TAB.SETTINGS && tab !== TAB.NOTICES && (
            <TextField
              size="small"
              placeholder={
                tab === TAB.SUBS ? "Search seller or email"
                  : tab === TAB.PAYMENTS ? "Search seller, ad or reference"
                    : "Search invoice, account or seller"
              }
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 240 }}
            />
          )}

          {tab === TAB.SUBS && (
            <FormControlLabel
              control={
                <Switch
                  checked={unbilledOnly}
                  onChange={(e) => { setUnbilledOnly(e.target.checked); setPage(0); }}
                />
              }
              label="Paid tier, no payment method"
            />
          )}

          <Box sx={{ flex: 1 }} />

          {tab === TAB.INVOICES && (
            <>
              <Typography variant="body2" color="text.secondary">
                {money(invoices.paid)} paid · {money(invoices.outstanding)} outstanding
              </Typography>
              <Tooltip title="Raises this month's subscription invoice for every seller on a paid tier who does not already have one. It does not charge anybody.">
                <Button size="small" variant="outlined" onClick={() => setConfirmRun("monthly")}>
                  Raise this month's invoices
                </Button>
              </Tooltip>
            </>
          )}

          {tab === TAB.NOTICES && (
            <Tooltip title="Marks anything past its due date overdue and flags accounts whose deadline has passed. It does not suspend anybody.">
              <Button size="small" variant="outlined" onClick={runCreditControl}>
                Run credit control
              </Button>
            </Tooltip>
          )}
        </Box>

        {tab === TAB.SETTINGS ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <BillingSettings />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={rowCount}
            loading={loading || localLoading}
            checkboxSelection={false}
            disableRowSelectionOnClick
            onRowDoubleClick={(p) => setOpenDealer(p.row.user_id || p.row.id)}
            sx={{ border: 0 }}
          />
        )}
      </Paper>

      <DealerBillingPanel
        userId={openDealer}
        open={!!openDealer}
        onClose={() => setOpenDealer(null)}
        onChanged={reloadCurrent}
      />

      <Dialog open={confirmRun === "monthly"} onClose={() => setConfirmRun(null)}>
        <DialogTitle>Raise this month's invoices?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This creates one invoice per seller on a paid tier for the current month, unless they already
            have one. Nobody is charged and no email goes out - it only raises the bills so they can be
            collected and chased.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRun(null)}>Cancel</Button>
          <Button variant="contained" onClick={runMonthly}>Raise them</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  summary: state.billingReducer.summary,
  subscriptions: state.billingReducer.subscriptions,
  totalSubscriptions: state.billingReducer.totalSubscriptions,
  payments: state.billingReducer.payments,
  totalPayments: state.billingReducer.totalPayments,
  loading: state.billingReducer.loading,
});

export default connect(mapStateToProps)(BillingOverview);
